import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { TopicRow } from "@/components/feature/ContentCards";
import { topicList, topicTags } from "@/mocks/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 4;

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag") ?? "全部";
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: "话题讨论 - 成年向内容社区 - 18禁",
    description:
      "18禁话题讨论区：剧集、游戏、漫画、分级制度与观看体验的成年向内容讨论，支持标签筛选与关键词搜索。",
    keywords: "18禁话题,成人内容讨论,剧集讨论,游戏讨论,漫画讨论,分级制度",
  });

  useEffect(() => {
    setPage(1);
  }, [activeTag, keyword]);

  const filtered = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    return topicList.filter((item) => {
      const matchesTag = activeTag === "全部" || item.tags.includes(activeTag);
      const haystack = `${item.title} ${item.summary} ${item.tags.join(" ")}`.toLowerCase();
      const matchesKeyword = !value || haystack.includes(value);
      return matchesTag && matchesKeyword;
    });
  }, [activeTag, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const setTag = (tag: string) => {
    const next = new URLSearchParams(searchParams);
    if (tag === "全部") next.delete("tag");
    else next.set("tag", tag);
    setSearchParams(next, { replace: true });
  };

  const reset = () => {
    setKeyword("");
    setSearchParams({}, { replace: true });
  };

  return (
    <div>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="cursor-pointer transition hover:text-primary-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" aria-hidden="true"></i>
            <span className="text-foreground-700">话题讨论</span>
          </nav>
          <h1 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[32px]">话题讨论</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            分享观感、交换片单、讨论行业规则。这里的话题由读者发起，所有内容均为演示数据。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid gap-8 lg:grid-cols-[248px_1fr] lg:gap-10">
          <aside className="space-y-5 lg:sticky lg:top-[118px] lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="mb-3 flex items-center gap-2 text-[11px] font-medium tracking-[0.18em] text-foreground-500">
                <i className="ri-price-tag-3-line text-sm text-primary-500" aria-hidden="true"></i>
                话题标签
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {topicTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTag(tag)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition ${
                      activeTag === tag
                        ? "border-primary-400 bg-primary-50 text-primary-700"
                        : "border-background-300/80 bg-background-50 text-foreground-600 hover:border-primary-200"
                    }`}
                  >
                    {tag === "全部" ? "全部" : `#${tag}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-accent-200 bg-accent-50 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold text-accent-900">
                <i className="ri-information-line text-sm" aria-hidden="true"></i>
                讨论须知
              </p>
              <ul className="mt-2.5 space-y-1.5 text-[11px] leading-relaxed text-accent-900/80">
                <li>· 仅限成年读者参与讨论</li>
                <li>· 请围绕作品与行业本身展开</li>
                <li>· 避免人身攻击与剧透未标注内容</li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="text-[11px] leading-relaxed text-foreground-500">
                话题区暂不支持发帖与回复，所有回复数、浏览量为演示数据。后续版本将开放读者投稿。
              </p>
            </div>
          </aside>

          <div className="min-w-0">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="relative flex w-full items-center sm:max-w-[320px]">
                <i className="ri-search-line absolute left-3 text-sm text-foreground-400" aria-hidden="true"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索话题关键词"
                  name="topic-q"
                  className="h-10 w-full rounded-md border border-background-300/80 bg-background-50 pl-9 pr-3 text-sm outline-none transition placeholder:text-foreground-400 focus:border-primary-400"
                />
              </label>
              <p className="text-xs text-foreground-500">
                共 <span className="font-medium text-foreground-800">{filtered.length}</span> 个话题
              </p>
            </div>

            {paged.length ? (
              <>
                <SectionHeading
                  eyebrow={activeTag === "全部" ? "ALL DISCUSSIONS" : `#${activeTag}`}
                  title={activeTag === "全部" ? "全部话题" : `${activeTag} 相关话题`}
                />
                <div className="space-y-3">
                  {paged.map((item, index) => (
                    <TopicRow key={item.id} item={item} index={(safePage - 1) * PAGE_SIZE + index} />
                  ))}
                </div>
                <div className="pt-8">
                  <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
                </div>
              </>
            ) : (
              <EmptyState
                title="没有找到相关话题"
                description="试试更换标签或关键词。也可以回到全部话题，看看大家最近在聊什么。"
                actionLabel="清除筛选"
                onAction={reset}
                secondaryLabel="返回首页"
                secondaryTo="/"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}